import type { Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useEffect } from 'react';
import { LayerId, SourceId } from '../constants';
import {
  type PlaceLinkPoint,
  placeLinkFeature,
  removePlaceLink,
  setPlaceLink,
} from '../utils/placeLink';

/**
 * While `origin` is set, a dotted line follows the pointer from that place.
 * Clearing the origin, or unmounting, removes the line.
 */
export function usePlaceCursorLink(
  map: RefObject<MapboxMap | null>,
  origin: PlaceLinkPoint | null,
): void {
  const longitude = origin?.longitude;
  const latitude = origin?.latitude;

  useEffect(() => {
    const current = map.current;
    if (!current || longitude == null || latitude == null) return;
    const from = { longitude, latitude };

    const onMove = (event: { lngLat: { lng: number; lat: number } }) => {
      setPlaceLink(
        current,
        SourceId.PLACE_CURSOR_LINK,
        LayerId.PLACE_CURSOR_LINK,
        placeLinkFeature(from, { longitude: event.lngLat.lng, latitude: event.lngLat.lat }),
      );
    };

    current.on('mousemove', onMove);
    return () => {
      current.off('mousemove', onMove);
      // The shell removes the map before its children clean up; a removed map has no layers.
      if (map.current !== current) return;
      removePlaceLink(current, SourceId.PLACE_CURSOR_LINK, LayerId.PLACE_CURSOR_LINK);
    };
  }, [map, longitude, latitude]);
}

/**
 * A dotted line from a selected location to the place it was created from.
 * Either end missing removes the line.
 */
export function usePlaceLocationLink(
  map: RefObject<MapboxMap | null>,
  from: PlaceLinkPoint | null,
  to: PlaceLinkPoint | null,
): void {
  const fromLng = from?.longitude;
  const fromLat = from?.latitude;
  const toLng = to?.longitude;
  const toLat = to?.latitude;

  useEffect(() => {
    const current = map.current;
    if (!current || fromLng == null || fromLat == null || toLng == null || toLat == null) return;
    setPlaceLink(
      current,
      SourceId.PLACE_LOCATION_LINK,
      LayerId.PLACE_LOCATION_LINK,
      placeLinkFeature(
        { longitude: fromLng, latitude: fromLat },
        { longitude: toLng, latitude: toLat },
      ),
    );
    return () => {
      // The shell removes the map before its children clean up; a removed map has no layers.
      if (map.current !== current) return;
      removePlaceLink(current, SourceId.PLACE_LOCATION_LINK, LayerId.PLACE_LOCATION_LINK);
    };
  }, [map, fromLng, fromLat, toLng, toLat]);
}
